import FeatureRequestColumn from "@/src/components/feature-requests/feature-request-column";
import {
  FEATURE_REQUEST_STATUSES,
  FeatureRequest,
} from "@/src/models/feature-request.model";
import { FeatureRequestScope } from "@/src/services/feature-request/feature-request.hooks";
import { useState } from "react";
import { LayoutChangeEvent, ScrollView, View } from "react-native";

type FeatureRequestBoardProps = Readonly<{
  scope: FeatureRequestScope;
  enabled: boolean;
  onCardPress: (request: FeatureRequest) => void;
}>;

// Vertical space the progress bar + gaps take above the columns.
const BOARD_CHROME_HEIGHT = 20;

/**
 * GitHub-Projects style board: one column per status, side by side, scrolling
 * horizontally. The bar on top tracks the horizontal position — on web the
 * native scrollbar is hidden, so it's the only hint more columns exist.
 */
export default function FeatureRequestBoard({
  scope,
  enabled,
  onCardPress,
}: FeatureRequestBoardProps) {
  const [boardHeight, setBoardHeight] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  const scrollable = contentWidth > viewportWidth && viewportWidth > 0;
  const thumbRatio = scrollable ? viewportWidth / contentWidth : 1;
  const maxScroll = Math.max(contentWidth - viewportWidth, 1);
  const thumbOffset = scrollable
    ? (scrollX / maxScroll) * (1 - thumbRatio)
    : 0;

  const columnMaxHeight =
    boardHeight > 0 ? boardHeight - BOARD_CHROME_HEIGHT : undefined;

  return (
    <View
      className="flex-1 gap-3 p-4 bg-chalk rounded-2xl border border-ash-3"
      onLayout={(e: LayoutChangeEvent) =>
        setBoardHeight(e.nativeEvent.layout.height - 32)
      }
    >
      <View className="h-1.5 w-full rounded-full bg-ash">
        {scrollable && (
          <View
            className="absolute h-1.5 rounded-full bg-ash-1"
            style={{
              width: `${thumbRatio * 100}%`,
              left: `${thumbOffset * 100}%`,
            }}
          />
        )}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={(e) => setScrollX(e.nativeEvent.contentOffset.x)}
        onLayout={(e) => setViewportWidth(e.nativeEvent.layout.width)}
        onContentSizeChange={(w) => setContentWidth(w)}
        contentContainerClassName="gap-4 items-start"
      >
        {FEATURE_REQUEST_STATUSES.map((status) => (
          <FeatureRequestColumn
            key={status}
            status={status}
            scope={scope}
            enabled={enabled}
            maxHeight={columnMaxHeight}
            onCardPress={onCardPress}
          />
        ))}
      </ScrollView>
    </View>
  );
}
